interface RangeSelectorProps {
  timeRange: [number, number];
  onValueChange: (value: [number, number]) => void;
}

export const RangeSelector = ({ timeRange, onValueChange }: RangeSelectorProps) => {
  const [left, right] = timeRange;

  const handleMouseDown = (edge: "left" | "right" | "center") => (e: React.MouseEvent) => {
    e.preventDefault();
    const container = e.currentTarget.closest("[data-range-container]") as HTMLElement;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startLeft = left;
    const startRight = right;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaPercent = ((moveEvent.clientX - startX) / rect.width) * 100;

      if (edge === "left") {
        const newLeft = Math.max(0, Math.min(startLeft + deltaPercent, right - 2));
        onValueChange([newLeft, right]);
      } else if (edge === "right") {
        const newRight = Math.min(100, Math.max(startRight + deltaPercent, left + 2));
        onValueChange([left, newRight]);
      } else {
        const width = startRight - startLeft;
        let newLeft = startLeft + deltaPercent;
        let newRight = startRight + deltaPercent;

        if (newLeft < 0) {
          newLeft = 0;
          newRight = width;
        }
        if (newRight > 100) {
          newRight = 100;
          newLeft = 100 - width;
        }
        onValueChange([newLeft, newRight]);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="absolute inset-0" data-range-container>
      <div
        className="absolute inset-y-0 left-0 bg-gray-400/30 backdrop-blur-[0.5px]"
        style={{ width: `${left}%` }}
      />
      <div
        className="absolute inset-y-0 right-0 bg-gray-400/30 backdrop-blur-[0.5px]"
        style={{ width: `${100 - right}%` }}
      />
      <div
        className="absolute inset-y-0 cursor-grab border-y border-blue-500/50 active:cursor-grabbing"
        style={{
          left: `${left}%`,
          width: `${right - left}%`,
        }}
        onMouseDown={handleMouseDown("center")}
      />
      <div
        className="absolute inset-y-0 z-10 w-2 cursor-ew-resize"
        style={{ left: `calc(${left}% - 8px)` }}
        onMouseDown={handleMouseDown("left")}
      >
        <div className="absolute inset-y-0 left-1/2 w-[3px] -translate-x-1/2 rounded-sm bg-blue-600 shadow-sm">
          <div className="absolute left-1/2 top-1/2 h-3 w-[1px] -translate-x-1/2 -translate-y-1/2 bg-white/60" />
        </div>
      </div>
      <div
        className="absolute inset-y-0 z-10 w-2 cursor-ew-resize"
        style={{ right: `calc(${100 - right}% - 8px)`, left: "auto" }}
        onMouseDown={handleMouseDown("right")}
      >
        <div className="absolute inset-y-0 left-1/2 w-[3px] -translate-x-1/2 rounded-sm bg-blue-600 shadow-sm">
          <div className="absolute left-1/2 top-1/2 h-3 w-[1px] -translate-x-1/2 -translate-y-1/2 bg-white/60" />
        </div>
      </div>
    </div>
  );
};
